import { useEffect, useState } from 'react';
import { CAlert, CCard, CCardBody, CCardHeader, CCol, CFormCheck, CFormSwitch, CRow, CSpinner } from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { useMe, useSetMe } from '../auth/AuthGate';
import { TeamCard } from './TeamCard';

type Role = Schemas['DomainRoleResponse'];
type Setting = Schemas['SettingResponse'];

/**
 * Nastavení — co dělám (nálepky) a co mi má chodit (notifikace).
 * <p>
 * Nálepky jsou tu první schválně: bez nich nedorazí dotaz ani úkol mířený na roli.
 * Obě části se kreslí z dat — nová role ani nové nastavení nevyžaduje úpravu téhle stránky.
 */
export function SettingsPage() {
  return (
    <>
      <h2 className="mb-4">Nastavení</h2>
      <CRow className="g-4">
        <CCol lg={6}><RolesCard /></CCol>
        <CCol lg={6}><NotificationSettings /></CCol>
      </CRow>
      <div className="mt-4"><TeamCard /></div>
    </>
  );
}

function RolesCard() {
  const me = useMe();
  const setMe = useSetMe();
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.GET('/api/v1/team/roles').then(({ data, error: problem }) =>
      data ? setRoles(data) : setError(problemMessage(problem)));
  }, []);

  async function toggle(code: string, checked: boolean) {
    const next = checked ? [...me.roles, code] : me.roles.filter((r) => r !== code);

    setSaving(true);
    setError(null);

    const { data, error: problem } = await api.PUT('/api/v1/team/me/roles', { body: { roleCodes: next } });

    setSaving(false);

    if (data) {
      // Hned promitnout do profilu - jinak by upozorneni na chybejici nalepky
      // zustalo viset az do obnoveni stranky.
      setMe({ ...me, roles: data });
    } else {
      setError(problemMessage(problem));
    }
  }

  return (
    <CCard>
      <CCardHeader>
        <strong>Co dělám</strong>
        {saving && <CSpinner size="sm" className="ms-2" />}
      </CCardHeader>
      <CCardBody>
        <p className="text-body-secondary">
          Podle toho vám chodí dotazy a úkoly mířené na roli — třeba „zeptej se vedení" nebo
          „čeká se na vývoj". Nic neomezuje, vidět i dělat můžete všechno.
        </p>
        {error && <CAlert color="danger">{error}</CAlert>}
        {!roles ? <CSpinner color="primary" /> : roles.map((role) => (
          <CFormCheck
            key={role.code}
            id={`role-${role.code}`}
            className="mb-2"
            disabled={saving}
            checked={me.roles.includes(role.code)}
            onChange={(e) => void toggle(role.code, e.target.checked)}
            label={
              <>
                <span className="fw-semibold">{role.name}</span>
                {role.description && <span className="text-body-secondary"> — {role.description}</span>}
              </>
            }
          />
        ))}
      </CCardBody>
    </CCard>
  );
}

function NotificationSettings() {
  const [settings, setSettings] = useState<Setting[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.GET('/api/v1/me/settings').then(({ data, error: problem }) =>
      data ? setSettings(data) : setError(problemMessage(problem)));
  }, []);

  async function change(key: string, value: boolean) {
    setError(null);

    const { data, error: problem } = await api.PUT('/api/v1/me/settings', { body: { [key]: value } });

    if (data) {
      setSettings(data);
    } else {
      setError(problemMessage(problem));
    }
  }

  if (!settings) {
    return error ? <CAlert color="danger">{error}</CAlert> : <CSpinner color="primary" />;
  }

  // Skupiny i popisky jsou v odpovedi - nove nastaveni se tu objevi samo. Skryta jsou pamet
  // aplikace (treba "pruvodce uz videl"), ne volba pro cloveka.
  const visible = settings.filter((s) => !s.isHidden);
  const groups = [...new Set(visible.map((s) => s.group))];

  return (
    <>
      {error && <CAlert color="danger">{error}</CAlert>}
      {groups.map((group) => (
        <CCard key={group} className="mb-4">
          <CCardHeader><strong>{group}</strong></CCardHeader>
          <CCardBody>
            {visible.filter((s) => s.group === group).map((setting) => (
              <div key={setting.key} className="mb-3">
                {setting.type === 'Boolean' && (
                  <CFormSwitch
                    id={setting.key}
                    checked={setting.value === true}
                    onChange={(e) => void change(setting.key, e.target.checked)}
                    label={<span className="fw-semibold">{setting.label}</span>}
                  />
                )}
                <div className="text-body-secondary small ms-5">{setting.description}</div>
              </div>
            ))}
          </CCardBody>
        </CCard>
      ))}
    </>
  );
}
